import type { Editor as TiptapEditor } from '@tiptap/react';
import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type RefObject,
  type KeyboardEvent,
} from 'react';
import { Button } from 'src/components/buttons-and-actions/button';
import { SplitActions } from 'src/components/buttons-and-actions/split-actions';
import { SimpleInput } from 'src/components/form/input';
import { useFloating } from 'src/hooks/use-floating';
import { flyoutClasses } from 'src/styles';
import { cn } from 'src/utils/cn';
import { normalizeUrl } from 'src/utils/normalize-url';

type LinkPopoverProps = {
  editor: TiptapEditor;
  isActive: boolean;
  setIsActive: (active: boolean) => void;
  triggerRef?: RefObject<HTMLElement | null>;
};

export const LinkPopover = ({
  editor,
  isActive,
  setIsActive,
  triggerRef,
}: LinkPopoverProps) => {
  const { refs, style } = useFloating({
    placement: 'bottom-start',
  });
  const linkHandler = useLinkHandler({
    editor,
    onClose: () => setIsActive(false),
  });
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (triggerRef?.current) {
      refs.setReference(triggerRef.current);
    }
  }, [triggerRef, refs]);

  useEffect(() => {
    if (!isActive) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isClickInsidePopover = popoverRef.current?.contains(target);
      const isClickOnTrigger = triggerRef?.current?.contains(target);
      // The "Apply and Open" and "Delete" actions live in a SplitActions
      // dropdown whose content is rendered through a portal on `document.body`,
      // so it is not contained by the popover element. Without this check the
      // `mousedown` would close (and unmount) the popover before the button's
      // `click` handler can run, making those actions appear to do nothing.
      const isClickInsideDropdown =
        target instanceof Element &&
        target.closest('[data-testid="dropdown-content"]') !== null;

      if (
        !isClickInsidePopover &&
        !isClickOnTrigger &&
        !isClickInsideDropdown
      ) {
        setIsActive(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActive, setIsActive, triggerRef]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        linkHandler.setLink();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setIsActive(false);
      } else if (e.key === 'Tab') {
        // Use setTimeout to check if focus moved outside the popover
        setTimeout(() => {
          if (
            !popoverRef.current?.contains(document.activeElement) &&
            !linkHandler.isActive
          ) {
            setIsActive(false);
          }
        }, 1);
      }
    },
    [linkHandler, setIsActive]
  );

  if (!isActive) return null;

  return (
    <div ref={popoverRef} className="relative">
      <div
        ref={refs.setFloating}
        style={style}
        className={cn(flyoutClasses, 'min-w-[27.9rem] px-xs py-sm')}
      >
        <LinkContent
          url={linkHandler.url}
          setUrl={linkHandler.setUrl}
          setLink={linkHandler.setLink}
          removeLink={linkHandler.removeLink}
          openLink={linkHandler.openLink}
          isActive={linkHandler.isActive}
          onKeyDown={handleKeyDown}
        />
      </div>
    </div>
  );
};

type LinkContentProps = {
  url: string;
  setUrl: (value: string) => void;
  setLink: () => void;
  removeLink: () => void;
  openLink: () => void;
  isActive: boolean;
  onKeyDown: (e: KeyboardEvent) => void;
};

function LinkContent({
  url,
  setUrl,
  setLink,
  removeLink,
  openLink,
  isActive,
  onKeyDown,
}: LinkContentProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current && !isActive) {
      inputRef.current.focus();
    }
  }, [isActive]);

  return (
    <div
      className={`
        flex items-center gap-lg
        legacy:gap-[1rem]
      `}
    >
      <SimpleInput
        ref={inputRef}
        id="link-url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Paste a link..."
        aria-label="Link URL"
        className="flex-1"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
      />

      <SplitActions mode="button-secondary" size="small">
        <Button onClick={setLink} disabled={!url.trim() && !isActive}>
          Apply
        </Button>
        <Button onClick={openLink} disabled={!url.trim() && !isActive}>
          Apply and Open
        </Button>
        {isActive && <Button onClick={removeLink}>Delete</Button>}
      </SplitActions>
    </div>
  );
}

type LinkHandlerProps = {
  editor: TiptapEditor;
  onClose?: () => void;
};

function useLinkHandler({ editor, onClose }: LinkHandlerProps) {
  const [url, setUrl] = useState<string>('');

  useEffect(() => {
    if (isLinkActive(editor)) {
      const { href } = editor.getAttributes('link');
      setUrl(href || '');
    } else {
      setUrl('');
    }
  }, [editor]);

  useEffect(() => {
    const updateLinkState = () => {
      if (isLinkActive(editor)) {
        const { href } = editor.getAttributes('link');
        setUrl(href || '');
      } else {
        setUrl('');
      }
    };

    editor.on('selectionUpdate', updateLinkState);
    return () => {
      editor.off('selectionUpdate', updateLinkState);
    };
  }, [editor]);

  const setLink = useCallback(() => {
    if (!url.trim()) return;

    const normalizedUrl = normalizeUrl(url.trim());
    const { selection } = editor.state;
    const isEmpty = selection.empty;

    let chain = editor.chain().focus();
    if (isLinkActive(editor)) {
      chain = chain.extendMarkRange('link');
    }
    chain = chain.setLink({ href: normalizedUrl });
    if (isEmpty) {
      chain = chain.insertContent({ type: 'text', text: normalizedUrl });
    }

    chain.run();

    onClose?.();
  }, [editor, onClose, url]);

  const removeLink = useCallback(() => {
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .unsetLink()
      .setMeta('preventAutolink', true)
      .run();

    setUrl('');

    onClose?.();
  }, [editor, onClose]);

  const openLink = useCallback(() => {
    if (!url.trim()) return;

    const normalizedUrl = normalizeUrl(url.trim());

    // If there's no active link or the URL has changed, apply the link first
    if (
      !isLinkActive(editor) ||
      editor.getAttributes('link').href !== normalizedUrl
    ) {
      const { selection } = editor.state;
      const isEmpty = selection.empty;

      let chain = editor.chain().focus();
      if (isLinkActive(editor)) {
        chain = chain.extendMarkRange('link');
      }
      chain = chain.setLink({ href: normalizedUrl });
      if (isEmpty) {
        chain = chain.insertContent({ type: 'text', text: normalizedUrl });
      }

      chain.run();
      setUrl(normalizedUrl);
    }

    window.open(normalizedUrl, '_blank', 'noopener,noreferrer');

    onClose?.();
  }, [editor, onClose, url]);

  return {
    url,
    setUrl,
    setLink,
    removeLink,
    openLink,
    canSet: canSetLink(editor),
    isActive: isLinkActive(editor),
  };
}

function isLinkActive(editor: TiptapEditor): boolean {
  if (editor.isDestroyed) return false;
  if (!editor.isEditable) return false;
  return editor.isActive('link');
}

function canSetLink(editor: TiptapEditor): boolean {
  if (editor.isDestroyed) return false;
  if (!editor.isEditable) return false;
  return editor.can().setMark('link');
}
